import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { TextFormField } from '~components/FormFields/TextFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import useAccount from '~hooks/useAccount/useAccount';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import TrucksV2 from '~pages/Sales/Orders/OrderFormV2/components/TrucksV2';
import { useStores } from '~store';

import type { ProjectFormSchemaInterface } from '../../projectFormSchema';
import { useProjectFormV2Context } from '../ProjectFormV2Context';

// Mirrors the legacy form. Render-only by design: every FF
// is read once in ProjectFormV2Provider and exposed via context.flags.* —
// the panel never reads flags directly. The service-class autocomplete list
// is hydrated by a Provider-side effect (ProjectFormV2Context.tsx) so the
// panel only consumes the context's serviceClassOptions.
//
// Field set rendered by this panel:
//   chargeToAccount                          (hidden when flags.tomlinsonOrderModal)
//   serviceClass                             (shown when showServiceClass)
//   orderQuantity                            (always)
//   orderUnitOfMeasure                       (always)
//   <TrucksV2 parent={defaultProject} requireTruckCount={false} />
//     -> truckCount, jobUnitOfMeasure, jobQuantity, equipmentType,
//        equipmentTypeGrossCapacity, equipmentTypeGrossCapacityUnitOfMeasure,
//        unitsPerHour (derived)
//   jobTime                                  (always)
//   chaseMissingTickets                      (shown when flags.driverAgent)
//   defaultDigitalTicketView                 (shown when showDefaultDigitalTicketView)
//   liveEta                                  (shown when flags.liveEtaEnabled)
export function OrderDefaultsPanel(): JSX.Element {
  const { control, formState } = useFormContext<ProjectFormSchemaInterface>();
  const {
    flags,
    showDefaultDigitalTicketView,
    showServiceClass,
    serviceClassOptions,
    defaultProject,
    watchedSelectedCompany,
  } = useProjectFormV2Context();
  const { userStore } = useStores();
  const { getAccountsByCompanyIdTypeahead } = useAccount();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();

  const currentUserCompany = userStore.userCompanies?.[0];

  // Mirrors the legacy form's fetchAccountsByCompanyId. The first
  // page prepends the current user's own company as a synthetic option so the
  // user can charge themselves. Subsequent pages forward verbatim.
  const fetchAccountsByCompanyId = async (
    additionalProps: Record<string, unknown> = {},
  ) => {
    const companyId = watchedSelectedCompany?.id;
    if (!companyId) {
      return { data: [], pagination: undefined };
    }
    const response = await getAccountsByCompanyIdTypeahead({
      companyId,
      accountTypes: [AccountType.CUSTOMER],
      ...additionalProps,
    });
    const isRequestingFirstPage = 'cursor' in additionalProps && !additionalProps.cursor;
    if (response && isRequestingFirstPage && currentUserCompany) {
      const data = [
        { id: currentUserCompany.id, name: currentUserCompany.legalName },
        ...response.data,
      ];
      return { ...response, data };
    }
    return response;
  };

  const trucksBlock = (
    <TrucksV2
      parent={defaultProject ?? undefined}
      requireTruckCount={false}
      shouldDirtyDerivedWrites
    />
  );

  return (
    <Card
      sx={{ p: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
      data-test-id="project-form-v2-order-defaults"
    >
      <Typography variant="form-section-title" sx={{ mb: 0 }}>
        {t('project.form.order_defaults')}
      </Typography>

      {(!flags.tomlinsonOrderModal || showServiceClass) && (
        <Grid container spacing={2} sx={{ py: 1 }}>
          {flags.tomlinsonOrderModal ? null : (
            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                asyncCallback={fetchAccountsByCompanyId}
                clearable
                control={control}
                errors={formState.errors}
                getLabel={(item) => item.name || ''}
                getValue={(item) => item.name}
                label={t('order.form.charge_to_account')}
                name="chargeToAccount"
              />
            </Grid>
          )}

          {showServiceClass && (
            <Grid size={{ xs: 6 }}>
              <AutocompleteFormField
                clearable
                control={control}
                errors={formState.errors}
                getLabel={(item) => item.name || ''}
                getValue={(item) => item.id}
                isRequired={false}
                label={t('form_fields.service_class')}
                list={serviceClassOptions}
                name="serviceClass"
              />
            </Grid>
          )}
        </Grid>
      )}

      <Typography sx={{ fontWeight: 600 }}>{t('form_fields.materials')}</Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 4 }}>
          <TextFormField
            control={control}
            errors={formState.errors}
            label={t('form_fields.ordered_quantity')}
            name="orderQuantity"
            type="number"
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <AutocompleteFormField
            clearable={true}
            control={control}
            errors={formState.errors}
            getLabel={(item) => item.name || ''}
            getValue={(item) => item?.id}
            label={t('form_fields.unit')}
            list={unitOfMeasureOptions}
            name="orderUnitOfMeasure"
          />
        </Grid>
      </Grid>

      <Typography sx={{ fontWeight: 600, mt: 1 }}>{t('form_fields.trucks')}</Typography>
      {trucksBlock}

      <Grid container spacing={2}>
        <Grid size={{ xs: 4 }}>
          <TextFormField
            control={control}
            errors={formState.errors}
            label={t('form_fields.truck_hours')}
            name="jobTime"
            type="number"
          />
        </Grid>

        {flags.driverAgent && (
          <Grid size={{ xs: 8 }}>
            <Tooltip title={t('project.form.chase_missing_tickets_tooltip')}>
              <Box>
                <FormFieldLabel label={t('project.form.ticket_chaser')} />
                <CheckboxInput
                  control={control}
                  label={t('project.form.text_drivers_for_missing_tickets')}
                  name="chaseMissingTickets"
                  sx={{
                    [`& .${formControlLabelClasses.root}`]: { mr: 0 },
                    [`& .${checkboxClasses.root}`]: { pr: '2px' },
                    [`& .${typographyClasses.root}`]: {
                      fontSize: '12px',
                      fontWeight: 400,
                    },
                  }}
                />
              </Box>
            </Tooltip>
          </Grid>
        )}
      </Grid>

      {showDefaultDigitalTicketView && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <CheckboxInput
              control={control}
              label={t('project.form.default_digital_ticket_view')}
              name="defaultDigitalTicketView"
              sx={{
                [`& .${formControlLabelClasses.root}`]: { mr: 0 },
                [`& .${checkboxClasses.root}`]: { pr: '2px' },
                [`& .${typographyClasses.root}`]: {
                  fontSize: '12px',
                  fontWeight: 400,
                },
              }}
            />
          </Grid>
        </Grid>
      )}

      {flags.liveEtaEnabled && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Tooltip title={t('project.form.enable_live_eta_tooltip')}>
              <Box data-test-id="project-form-v2-live-eta">
                <CheckboxInput
                  control={control}
                  label={t('project.form.enable_live_eta')}
                  name="liveEta"
                  sx={{
                    [`& .${formControlLabelClasses.root}`]: { mr: 0 },
                    [`& .${checkboxClasses.root}`]: { pr: '2px' },
                    [`& .${typographyClasses.root}`]: {
                      fontSize: '12px',
                      fontWeight: 400,
                    },
                  }}
                />
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      )}
    </Card>
  );
}
